import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchCurrentUser } from './store/slices/authSlice';
import { tokenStore } from './services/api';
import { fetchBranding } from './store/slices/settingsSlice';
import { initLanguage, setUserLanguage } from './store/slices/languageSlice';
import { takePendingLanguageChoice } from './i18n';
import { canAccessRoute } from './utils/permissions';
import { TEMPLATE_LIBRARY_ENABLED } from './config/features';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ContractList from './pages/ContractList';
import ContractDetail from './pages/ContractDetail';
import ContractForm from './pages/ContractForm';
import SchemaList from './pages/SchemaList';
import Approvals from './pages/Approvals';
import FlowManagement from './pages/FlowManagement';
import FlowBuilder from './pages/FlowManagement/FlowBuilder';
import AuditLog from './pages/AuditLog';
import ActivityReport from './pages/ActivityReport';
import Settings from './pages/Settings';
import BrandingStudio from './pages/BrandingStudio';
import AdminPanel from './pages/AdminPanel';
import HRPanel from './pages/HRPanel';
import Permissions from './pages/Permissions/Permissions';
import DocumentEditor from './pages/DocumentEditor';
import VersionCompare from './pages/VersionCompare';
import FieldsManager from './pages/FieldsManager';
import SignatureIntegrations from './pages/SignatureIntegrations/SignatureIntegrations';
import TaskReassignment from './pages/TaskReassignment';
import SigningPage from './pages/SigningPage/SigningPage';
import VerifyContract from './pages/VerifyContract/VerifyContract';
import ExternalReview from './pages/ExternalReview/ExternalReview';
import ThankYou from './pages/ExternalReview/ThankYou';
import ContractTemplates from './pages/ContractTemplates/ContractTemplates';
import TemplateLibrary from './pages/TemplateLibrary';
import DocumentLibrary from './pages/DocumentLibrary';
import ContractTemplateEditor from './pages/ContractTemplateEditor/ContractTemplateEditor';
import Subscription from './pages/Subscription/Subscription';
import Landing from './pages/Landing';
import ImpersonationHandoff from './pages/ImpersonationHandoff';
import DelegationHandoff from './pages/DelegationHandoff';
import ChangePasswordRequired from './pages/ChangePasswordRequired';
import { isTenantSubdomain } from './utils/tenantDetection';

function AppLoader() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: '#f8fafc',
      flexDirection: 'column',
      gap: '16px',
    }}>
      <div style={{
        width: '36px',
        height: '36px',
        borderRadius: '10px',
        background: '#6366f1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'pulse 1.5s ease-in-out infinite',
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(0.95); }
        }
      `}</style>
    </div>
  );
}

function PrivateRoute({ children }) {
  const { isAuthenticated, loading, initializing, user } = useSelector((state) => state.auth);
  if (initializing || loading) return <AppLoader />;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.mustChangePassword) return <Navigate to="/change-password-required" replace />;
  return children;
}

function ForcedPasswordChangeRoute() {
  const { isAuthenticated, loading, initializing, user } = useSelector((state) => state.auth);
  if (initializing || loading) return <AppLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!user?.mustChangePassword) return <Navigate to="/dashboard" replace />;
  return <ChangePasswordRequired />;
}

function LandingOrRedirect() {
  const { isAuthenticated, loading, initializing } = useSelector((state) => state.auth);
  if (initializing || loading) return <AppLoader />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  if (isTenantSubdomain()) return <Navigate to="/login" replace />;
  return <Landing />;
}

function PermissionDenied() {
  const { t } = useTranslation();
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '60vh', gap: '16px', textAlign: 'center',
      padding: '40px 24px',
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: 16,
        background: 'var(--danger-bg, #fef2f2)',
        border: '1px solid var(--danger-border, #fecaca)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
          stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </div>
      <div>
        <h2 style={{ margin: '0 0 8px', fontSize: '1.25rem', fontWeight: 700, color: '#111827' }}>
          {t('app.accessDenied')}
        </h2>
        <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem', maxWidth: 360 }}>
          {t('app.noPermission')}
        </p>
      </div>
      <a href="/dashboard" style={{
        marginTop: 8, padding: '8px 20px', borderRadius: 8,
        background: 'var(--accent, #6366f1)', color: 'white',
        textDecoration: 'none', fontSize: '0.875rem', fontWeight: 600,
      }}>
        {t('app.backToDashboard')}
      </a>
    </div>
  );
}

function RBACRoute({ children, path }) {
  const { user } = useSelector((state) => state.auth);
  if (!user) return <Navigate to="/login" />;
  if (!canAccessRoute(user.role, path, user.effectivePermissions || null)) {
    return <PermissionDenied />;
  }
  return children;
}

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settings);

  useEffect(() => {
    // The impersonation handoff page bootstraps its own session — skip here,
    // otherwise App's fetchCurrentUser (which uses the owner's localStorage
    // token) races with the handoff flow.
    if (window.location.pathname.startsWith('/impersonate/handoff')) return;

    const token = tokenStore().getItem('accessToken');
    if (token && !isAuthenticated) {
      dispatch(fetchCurrentUser());
    }
  }, [dispatch, isAuthenticated]);

  // Fetch branding as soon as user is authenticated (no flash of default data)
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchBranding());
    }
  }, [dispatch, isAuthenticated]);

  // Initialize language whenever user or tenant settings change
  useEffect(() => {
    if (user) {
      const tenantDefaultLanguage = settings?.config?.defaultLanguage || null;
      dispatch(initLanguage({
        userPreferredLanguage: user.preferredLanguage || null,
        tenantDefaultLanguage,
      }));

      // A language picked on the login screen had no profile to be saved to
      // yet. Persist it now so it survives the next login on any device —
      // without this, signing in would revert the user's own pick.
      const pending = takePendingLanguageChoice();
      if (pending && pending !== user.preferredLanguage) {
        dispatch(setUserLanguage(pending));
      }
    }
  }, [dispatch, user, settings?.config?.defaultLanguage]);

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/impersonate/handoff" element={<ImpersonationHandoff />} />
      <Route path="/delegation/handoff" element={<DelegationHandoff />} />
      <Route path="/change-password-required" element={<ForcedPasswordChangeRoute />} />

      {/* Public routes — external party review (no auth required) */}
      <Route path="/external-review/:accessToken" element={<ExternalReview />} />
      <Route path="/external-review/:accessToken/thank-you" element={<ThankYou />} />

      {/* Public route — contract signing (token-based, no auth required) */}
      <Route path="/sign/:accessToken" element={<SigningPage />} />

      {/* Public route — QR verification of approved contract documents */}
      <Route path="/verify/:code" element={<VerifyContract />} />

      {/* ── Full-screen editor routes (no sidebar/header) ── */}
      <Route path="/contracts/:id/documents" element={<PrivateRoute><RBACRoute path="/contracts/edit"><DocumentEditor /></RBACRoute></PrivateRoute>} />
      <Route path="/contracts/:id/documents/:documentId/editor" element={<PrivateRoute><RBACRoute path="/contracts/edit"><DocumentEditor /></RBACRoute></PrivateRoute>} />
      <Route path="/contracts/:id/documents/:documentId/compare" element={<PrivateRoute><RBACRoute path="/contracts"><VersionCompare /></RBACRoute></PrivateRoute>} />
      <Route path="/contract-types/:contractTypeId/fields" element={<PrivateRoute><RBACRoute path="/admin"><FieldsManager /></RBACRoute></PrivateRoute>} />
      <Route path="/contract-templates/:id/edit" element={<PrivateRoute><RBACRoute path="/contract-templates"><ContractTemplateEditor /></RBACRoute></PrivateRoute>} />
      <Route path="/fields-manager" element={<PrivateRoute><RBACRoute path="/admin"><FieldsManager /></RBACRoute></PrivateRoute>} />
      <Route path="/fields-manager/:schemaId" element={<PrivateRoute><RBACRoute path="/admin"><FieldsManager /></RBACRoute></PrivateRoute>} />

      {/* ── Public landing page ── */}
      <Route path="/" element={<LandingOrRedirect />} />

      {/* ── Standard layout routes ── */}
      <Route element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="contracts" element={<RBACRoute path="/contracts"><ContractList /></RBACRoute>} />
        <Route path="contracts/new" element={<RBACRoute path="/contracts/new"><ContractForm /></RBACRoute>} />
        <Route path="contracts/:id" element={<RBACRoute path="/contracts"><ContractDetail /></RBACRoute>} />
        <Route path="contracts/:id/edit" element={<RBACRoute path="/contracts/edit"><ContractForm /></RBACRoute>} />
        <Route path="contract-templates" element={<RBACRoute path="/contract-templates"><ContractTemplates /></RBACRoute>} />
        <Route path="documents" element={<RBACRoute path="/documents"><DocumentLibrary /></RBACRoute>} />
        {TEMPLATE_LIBRARY_ENABLED && (
          <Route path="template-library" element={<RBACRoute path="/template-library"><TemplateLibrary /></RBACRoute>} />
        )}
        <Route path="schemas" element={<RBACRoute path="/schemas"><SchemaList /></RBACRoute>} />
        <Route path="approvals" element={<RBACRoute path="/approvals"><Approvals /></RBACRoute>} />
        <Route path="activity-report" element={<RBACRoute path="/activity-report"><ActivityReport /></RBACRoute>} />
        <Route path="flow-management" element={<RBACRoute path="/flow-management"><FlowManagement /></RBACRoute>} />
        <Route path="flow-management/new" element={<RBACRoute path="/flow-management"><FlowBuilder /></RBACRoute>} />
        <Route path="flow-management/:id/edit" element={<RBACRoute path="/flow-management"><FlowBuilder /></RBACRoute>} />
        <Route path="audit" element={<RBACRoute path="/audit"><AuditLog /></RBACRoute>} />
        <Route path="profile" element={<Settings variant="account" />} />
        <Route path="settings" element={<RBACRoute path="/settings"><Settings variant="workspace" /></RBACRoute>} />
        <Route path="branding-studio" element={<BrandingStudio />} />
        <Route path="admin" element={<RBACRoute path="/admin"><AdminPanel /></RBACRoute>} />
        <Route path="hr" element={<RBACRoute path="/hr"><HRPanel /></RBACRoute>} />
        <Route path="permissions" element={<RBACRoute path="/permissions"><Permissions /></RBACRoute>} />
        {/* User Groups now lives as a tab inside the HR panel; keep the old
            URL working for bookmarks and deep links. */}
        <Route path="groups" element={<Navigate to="/hr?tab=groups" replace />} />
        <Route path="signature-integrations" element={<RBACRoute path="/signature-integrations"><SignatureIntegrations /></RBACRoute>} />
        <Route path="task-reassignment" element={<RBACRoute path="/task-reassignment"><TaskReassignment /></RBACRoute>} />
        <Route path="subscription" element={<RBACRoute path="/subscription"><Subscription /></RBACRoute>} />
      </Route>
    </Routes>
  );
}

export default App;
